import { useState } from 'react';
import {
  Link,
  useLocation,
} from 'react-router-dom';
import {
  ChevronDown,
  ChevronRight,
  Utensils,
} from 'lucide-react';
import { cn } from '@/lib/classnames';
import { usePermissions } from '@/hooks/use-auth';
import type { NavItem } from '@/config/navigation';
import type { Permission } from '@/types/auth';

interface SidebarProps {
  readonly navigation: NavItem[];
  readonly collapsed?: boolean;
}

export function Sidebar({
  navigation,
  collapsed = false,
}: SidebarProps) {
  return (
    <aside
      className={cn(
        'flex h-full flex-col border-r bg-background transition-all duration-300',
        collapsed ? 'w-16' : 'w-64'
      )}
    >
      <div className="flex h-14 items-center border-b px-4">
        <Utensils className="h-6 w-6 text-primary" />
        {!collapsed && <span className="ml-2 text-lg font-semibold">GalleyOS</span>}
      </div>
      <nav className="flex-1 overflow-y-auto p-2">
        <ul className="space-y-1">
          {navigation.map((item) => (
            <NavItemComponent key={item.id} item={item} collapsed={collapsed} />
          ))}
        </ul>
      </nav>
    </aside>
  );
}

interface NavItemComponentProps {
  readonly item: NavItem;
  readonly collapsed: boolean;
  readonly depth?: number;
}

function NavItemComponent({
  item,
  collapsed,
  depth = 0,
}: NavItemComponentProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const location = useLocation();
  const { canAny } = usePermissions();

  if (item.permissions && !canAny(item.permissions as Permission[])) {
    return null;
  }

  const hasChildren = item.children && item.children.length > 0;
  // For leaf items (no children), use exact match only
  // For parent items, also match if current path starts with item href
  const isActive = hasChildren
    ? location.pathname === item.href || location.pathname.startsWith(item.href + '/')
    : location.pathname === item.href;
  const IconComponent = item.icon;

  if (collapsed) {
    return (
      <li>
        <Link
          to={item.href}
          className={cn(
            'flex h-10 w-10 items-center justify-center rounded-md transition-colors',
            isActive
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
          )}
          title={item.label}
        >
          <IconComponent className="h-5 w-5" />
        </Link>
      </li>
    );
  }

  return (
    <li>
      <div
        className={cn(
          'flex items-center rounded-md transition-colors',
          isActive && !hasChildren
            ? 'bg-primary text-primary-foreground'
            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
        )}
        style={{ paddingLeft: `${depth * 12}px` }}
      >
        {hasChildren ? (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex flex-1 items-center gap-3 px-3 py-2"
          >
            <IconComponent className="h-5 w-5 shrink-0" />
            <span className="flex-1 text-left text-sm">{item.label}</span>
            {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>
        ) : (
          <Link to={item.href} className="flex flex-1 items-center gap-3 px-3 py-2">
            <IconComponent className="h-5 w-5 shrink-0" />
            <span className="text-sm">{item.label}</span>
          </Link>
        )}
      </div>
      {hasChildren && isExpanded && (
        <ul className="mt-1 space-y-1">
          {item.children?.map((child) => (
            <NavItemComponent key={child.id} item={child} collapsed={collapsed} depth={depth + 1} />
          ))}
        </ul>
      )}
    </li>
  );
}
